import GameModel from "../../GameModel";
import GridTile from "../grid/GridTile";
import { TileTypeRarity } from "../grid/TileTypeRarity";
import BigNum from "../math/BigNum";

export default class Statistics extends GameModel {
    constructor() {
        super();

        // NOTE: If you add a new property, you MUST add it to the resetFull() method

        /**
         * @constant
         * @type {BigNum}
         */
        this.totalOre = new BigNum(0);
        /**
         * @constant
         * @type {BigNum}
         */
        this.totalGold = new BigNum(0);
        /**
         * @constant
         * @type {BigNum}
         */
        this.totalRuby = new BigNum(0);
        /**
         * @constant
         * @type {BigNum}
         */
        this.totalPrestigePoints = new BigNum(0);

        /**
         * @constant
         * @type {number}
         */
        this.maxDepth = 0;

        /**
         * Ore from mined upgrades
         * @constant
         * @type {BigNum}
         */
        this.upgradeOre = new BigNum(0);

        /**
         * @constant
         * @type {number}
         */
        this.totalTurns = 0;
        
        ///////////////////////////////////////
        // Total blocks mined
        ///////////////////////////////////////

        /**
         * @constant
         * @type {BigNum}
         */
        this.tilesMined = new BigNum(0);

        ///////////////////////////////////////
        // Blocks by type
        ///////////////////////////////////////

        /**
         * @constant
         * @type {BigNum}
         */
        this.tilesMinedCommon = new BigNum(0);
        /**
         * @constant
         * @type {BigNum}
         */
        this.tilesMinedOre = new BigNum(0);
        /**
         * @constant
         * @type {BigNum}
         */
        this.tilesMinedGold = new BigNum(0);
        /**
         * @constant
         * @type {BigNum}
         */
        this.tilesMinedRuby = new BigNum(0);
        /**
         * @constant
         * @type {BigNum}
         */
        this.tilesMinedUpgrade = new BigNum(0);


        ///////////////////////////////////////
        // Blocks by skill
        ///////////////////////////////////////

        /**
         * @constant
         * @type {BigNum}
         */
        this.tilesMinedLaser = new BigNum(0);
        /**
         * @constant
         * @type {BigNum}
         */
        this.tilesMinedDrill = new BigNum(0);
        // /**
        //  * @constant
        //  * @type {BigNum}
        //  */
        // this.tilesMinedDynamite = new BigNum(0);
        /**
         * @constant
         * @type {BigNum}
         */
        this.tilesMinedOrbit = new BigNum(0);
        /**
         * @constant
         * @type {BigNum}
         */
        this.tilesMinedFissure = new BigNum(0);
        /**
         * @constant
         * @type {BigNum}
         */
        this.tilesMinedLightning = new BigNum(0);
        // /**
        //  * @constant
        //  * @type {BigNum}
        //  */
        // this.tilesMinedMinion = new BigNum(0);
        /**
         * @constant
         * @type {BigNum}
         */
        this.tilesMinedMissile = new BigNum(0);        

        /**
         * This isn't a stat. It's just here to speed up the math.
         * @constant
         * @type {BigNum}
         */
        this.one = new BigNum(1);
    }

    /**
     * @param {number} depth 
     */
    tileDepth(depth) {
        if (depth > this.maxDepth) {
            this.maxDepth = depth;
        }
    }

    /**
     * @param {GridTile} tile 
     */
    onTileMined(tile) {
        this.tilesMined.add(this.one);

        ////////////////////////
        // Type
        ////////////////////////

        const tileTypeRarity = tile.tileType.rarityModifier;
        switch (tileTypeRarity) {
            case TileTypeRarity.COMMON:
                this.tilesMinedCommon.add(this.one);
                break;
            case TileTypeRarity.ORE:
                this.tilesMinedOre.add(this.one);
                break;
            case TileTypeRarity.GOLD:
                this.tilesMinedGold.add(this.one);
                break;
            case TileTypeRarity.RUBY:
                this.tilesMinedRuby.add(this.one);
                break;
            case TileTypeRarity.UPGRADE:
                this.tilesMinedUpgrade.add(this.one);
                break;
        }
    }

    /**
     * @param {number} turns 
     */
    incrementTurns(turns) {
        this.totalTurns += turns
    }

    /**
     * @param {BigNum} ore 
     */
    incrementOre(ore) {
        this.totalOre.add(ore);
    }
    /**
     * @param {BigNum} ore 
     */
    incrementUpgradeOre(ore) {
        this.upgradeOre.add(ore);
    }

    /**
     * @param {BigNum} gold 
     */
    incrementGold(gold) {
        this.totalGold.add(gold);
    }
    /**
     * @param {BigNum} ruby 
     */
    incrementRuby(ruby) {
        this.totalRuby.add(ruby);
    }

    /**
     * @param {BigNum} prestigePoints 
     */
    incrementPrestigePoints(prestigePoints) {
        this.totalPrestigePoints.add(prestigePoints);
    }

    incrementMinedByLaser() {
        this.tilesMinedLaser.add(this.one);
    }
    incrementMinedByDrill() {
        this.tilesMinedDrill.add(this.one);
    }
    incrementMinedByOrbit() {
        this.tilesMinedOrbit.add(this.one);
    }
    incrementMinedByFissure() {
        this.tilesMinedFissure.add(this.one);
    }
    incrementMinedByLightning() {
        this.tilesMinedLightning.add(this.one);
    }
    incrementMinedByMissile() {
        this.tilesMinedMissile.add(this.one);
    }

    ////////////////////////////////////
    // GameModel
    ////////////////////////////////////

    /**
     * @override
     */
    resetFull() {
        this.totalOre.setZero();
        this.totalGold.setZero();
        this.totalRuby.setZero();
        this.totalPrestigePoints.setZero();

        this.upgradeOre.setZero();
        this.maxDepth = 0;

        this.totalTurns = 0;

        ///////////////////////////////////////
        // Total blocks mined
        ///////////////////////////////////////

        this.tilesMined.setZero();

        ///////////////////////////////////////
        // Blocks by type
        ///////////////////////////////////////

        this.tilesMinedCommon.setZero();
        this.tilesMinedOre.setZero();
        this.tilesMinedGold.setZero();
        this.tilesMinedRuby.setZero();
        this.tilesMinedUpgrade.setZero();

        ///////////////////////////////////////
        // Blocks by skill
        ///////////////////////////////////////

        this.tilesMinedLaser.setZero();
        this.tilesMinedDrill.setZero();
        //this.tilesMinedDynamite.setZero();
        this.tilesMinedOrbit.setZero();
        this.tilesMinedFissure.setZero();
        this.tilesMinedLightning.setZero();
        //this.tilesMinedMinion.setZero();
        this.tilesMinedMissile.setZero();
    }

    /**
     * @override
     */
    resetForPrestige() {
        this.resetFull();
    }

}